import { Link } from 'components/Link';
import { CustomSignupProductType } from 'pages/SignUp/types';
import { GRAFANA_COM_DOMAIN, PUBLIC_URL } from 'utils/consts';

export type CustomProductRedirects = (typeof customSignupProducts)[number]['redirect'];

/** How to use this file:
 * Below are the custom signups contained within this spreadsheet
 * https://docs.google.com/spreadsheets/d/1kUui1g2ouTMhNH_H1zX_SonZtnj0rER4YXSTC2pQYf4/edit?pli=1#gid=821683971
 * which is managed by GTM, starting with Chieu Pham. To update copy on a signup page
 * simply update this file. Here's how to do that:
 *
 * Required fields:
 * `key`: the "current logic" column and what will be parsed both as a string and as query params
 *        in the signUpVariant function in the file `customSignupHelpers.tsx`,
 * `title`: the copy that will be most prominent on the custom signup form,
 * `bullets`: an array of bullets,
 * `redirect`: the url the user is redirected to after verfication and initialization,
 * `imgAlt`: self-explanatory (the word "logo" is appended to this in the `CustomSignup` component)
 * `imgSrc`: source of where the logo image comes from
 *
 * Optional fields:
 * `isHeroImg`: the default logo is an icon size. When set to true, it renders a large size logo
 * `isNumber`: switch the checkmark bullets to numbered bullets
 * `description`: text that appears below the title
 * `footer`: text that appears below the bullet points
 */

export const defaultSignupProduct = ({
  key,
  redirect,
}: {
  key: string;
  redirect: string;
}): CustomSignupProductType => ({
  key,
  title: 'The (actually useful) free forever plan',
  description: 'Full-stack observability with AI built in.',
  bullets: [
    'Grafana Assistant — AI for queries, dashboards, and troubleshooting',
    '10k Prometheus metrics + Adaptive Telemetry',
    '50 GB logs',
    '50 GB traces',
    '50 GB profiles',
    '100k Synthetics executions',
    '500 VUh k6 testing',
    '50k Frontend sessions',
    '14-day retention · 3 active users',
  ],
  imgAlt: 'Grafana Cloud',
  imgSrc: `${PUBLIC_URL}/img/signup/hero/multiverse.svg`,
  isHeroImg: true,
  redirect,
});

export const datasourceSignupProduct = ({ key, name }: { key: string; name: string }): CustomSignupProductType => ({
  key,
  title: `Connect ${name} to Grafana Cloud — free forever.`,
  bullets: [
    `Query, correlate, and alert on your ${name} data alongside your full observability stack`,
    'Grafana Assistant — AI that correlates your data, answers questions, and builds queries',
    'Free forever — 10k Prometheus metrics, 50 GB logs, traces, and profiles',
  ],
  imgAlt: name,
  imgSrc: `${GRAFANA_COM_DOMAIN}/api/plugins/${key}/versions/latest/logos/large`,
  redirect: `connections/datasources/${key}`,
});

export const pluginSignupProduct = ({ key, name }: { key: string; name: string }): CustomSignupProductType => {
  const standard = defaultSignupProduct({
    key,
    redirect: `plugins/${key}`,
  });

  return {
    ...standard,
    description: `Get started with ${name} in Grafana Cloud.`,
    bullets: ['Connect 150+ data sources — plus panels and apps to extend Grafana', ...standard.bullets],
  };
};

const integrationSignupProduct = ({
  key,
  name,
  integration,
  image,
}: {
  key: string;
  name: string;
  integration: string;
  image: string;
}): CustomSignupProductType => ({
  key,
  title: `Monitor ${name} with full-stack observability — free forever.`,
  bullets: [
    `Pre-built dashboards and alerts for ${name}, ready in minutes`,
    `Grafana Assistant — AI that correlates your ${name} metrics with logs, traces, and profiles`,
    'Free forever — 10k Prometheus metrics, 50 GB logs, traces, and profiles',
  ],
  imgAlt: name,
  imgSrc: `https://storage.googleapis.com/grafanalabs-integration-logos/${image}`,
  redirect: `connections/add-new-connection/${integration}`,
});

export const dashboardSignupProduct = ({
  key,
  redirect,
}: {
  key: string;
  redirect: string;
}): CustomSignupProductType => ({
  key,
  title: 'Skip the blank canvas. Import from 1,000+ community dashboards.',
  bullets: [
    'Connect any data source — Prometheus, Loki, and hundreds of plugins and integrations',
    'Grafana Assistant — AI that correlates your data, answers questions, and builds queries',
    'Version and deploy dashboards as code with Git Sync',
  ],
  imgAlt: 'Dashboards',
  imgSrc: `${PUBLIC_URL}/img/signup/hero/dashboards.svg`,
  isHeroImg: true,
  redirect,
});

const marketplaceSignupProduct = ({
  key,
  provider,
  name,
}: {
  key: string;
  provider: string;
  /** Provider display name without the "Marketplace" suffix, e.g. "AWS", "Google Cloud". */
  name: string;
}): CustomSignupProductType => ({
  key,
  title: `Get started with Grafana Cloud through the ${name} Marketplace`,
  bullets: [
    'Full-stack observability with AI built in: metrics, logs, traces, and profiles',
    `Unified billing: Grafana Cloud usage appears on your ${name} bill`,
    `Get your stack at any Grafana Cloud ${name} region`,
  ],
  imgAlt: `${name} Marketplace`,
  imgSrc: `${PUBLIC_URL}/img/signup/marketplace-${provider}.svg`,
  redirect: `marketplaces/${provider}/signup/select-org`,
  // loginRedirect matches redirect: an existing (org-having) user who arrives via a marketplace
  // link still needs the marketplace org-select wizard to pick which org owns the subscription.
  loginRedirect: `marketplaces/${provider}/signup/select-org`,
  isPortalRedirect: true,
  // Recorded as the sign-up source (via sessionStorage `signupForm`, see CreateUserPage) so these
  // sign-ups are attributed to the marketplace. Not persisted with the rest of the variant.
  signupForm: `marketplace_${provider}`,
  // The marketplace flow provisions the org/stack itself, so auth must not create a trial org.
  skipOrgCreation: true,
});

export const customSignupProducts: CustomSignupProductType[] = [
  // #region Grafana products + features (alphabetical order by key)
  {
    key: 'agent-observability',
    title: 'Observe, evaluate, and continuously improve production AI agents',
    bullets: [
      'Replay prompts, tool calls, and responses to debug agent decisions in context',
      'Run live evaluations to catch hallucinations, unsafe output, and performance regressions as they happen',
      'Track latency, token usage, and spend by agent, run, and model',
      'Compare prompts, models, and tool changes with experiments before deploying updates',
      'Free forever — 30k generations/month + 25M tokens/month for system-initiated usage (Evals & Guards)',
    ],
    imgAlt: 'Agent Observability',
    imgSrc: `${PUBLIC_URL}/img/signup/agent-observability.svg`,
    redirect: `a/grafana-agento11y-app`,
  },
  {
    key: 'ai-assistant',
    title: 'Stop digging through dashboards. Ask your data.',
    bullets: [
      'Generate PromQL and LogQL from a natural-language prompt — across your own labels and series',
      'Triage incidents faster: ask what changed, get a ranked list of suspect services with the panels to prove it',
      'Build dashboards and alerts in one prompt — no panel-by-panel construction',
      'Grafana Assistant grounds every answer in your data, with citations to the source panel, query, or runbook',
      'Free forever — 3 active AI users per month with 40M tokens a month each',
    ],
    imgAlt: 'Grafana Assistant',
    imgSrc: `${PUBLIC_URL}/img/signup/assistant.svg`,
    redirect: `a/grafana-assistant-app`,
  },
  {
    key: 'application-observability',
    title: 'Find and fix application issues faster — free forever.',
    bullets: [
      'Correlate metrics, logs, and traces in one view',
      'AI-powered root cause analysis via Knowledge Graph',
      'OpenTelemetry and Prometheus native — no lock-in',
      'Pre-built service maps, dashboards, and SLO tracking',
      'Free forever access to Application Observability for up to 2,232 host hours/month',
    ],
    imgAlt: 'Application Observability',
    imgSrc: `${PUBLIC_URL}/img/signup/application-observability.svg`,
    redirect: `a/grafana-app-observability-app/services`,
  },
  {
    key: 'bi',
    title: 'Get started with Grafana BI',
    description: 'Business intelligence on the observability platform your teams already know.',
    bullets: [
      'Bring business and operational data together in one place',
      'Build reports and dashboards with the Grafana experience your teams already use',
      'Share insights across teams with Grafana access controls',
      'No credit card required',
    ],
    imgAlt: 'Grafana BI',
    imgSrc: `${PUBLIC_URL}/img/signup/bi.svg`,
    redirect: `signup/bi/select-org`,
    // loginRedirect matches redirect: an existing (org-having) user arriving from grafana.bi still
    // needs the portal's org step before a BI stack can be created.
    loginRedirect: `signup/bi/select-org`,
    isPortalRedirect: true,
    // FIXME: Validate with Marketing before GA whether this should be `bi_trial` for consistency with Cloud.
    signupForm: 'bi',
    // The portal flow resolves the org and provisions the BI stack, so auth must not create a
    // trial org/stack that would pollute the org step.
    skipOrgCreation: true,
    // Two-letter key: without this it would match inside `database-observability` and friends.
    exactKeyMatch: true,
  },
  {
    key: 'database-observability',
    title: 'Stop guessing why the database is slow — free forever.',
    bullets: [
      'Debug query latency with precision — explain plans, wait events, and per-query samples for MySQL and PostgreSQL',
      'Connect SQL latency to infrastructure — overlay query performance with CPU, memory, and disk I/O metrics',
      'Grafana Assistant — AI that suggests CREATE INDEX statements and query rewrites, ranked by impact',
      'Free forever — up to 2,232 host hours/month across 3 database hosts',
    ],
    imgAlt: 'Database Observability',
    imgSrc: `${PUBLIC_URL}/img/signup/database-observability.svg`,
    redirect: `a/grafana-dbo11y-app`,
  },
  {
    key: 'enterprise-trial',
    title: 'Try Grafana Enterprise free for 14 days',
    bullets: [
      'Full Grafana Enterprise feature set — enterprise auth (SAML, LDAP, OAuth), RBAC, and audit logs',
      'Enterprise plugins — Splunk, Snowflake, Databricks, Dynatrace, and more',
      '14-day trial — 1 admin, 25 viewers, 3 concurrent user sessions',
      'No credit card required',
    ],
    imgAlt: 'Grafana Enterprise',
    imgSrc: `${PUBLIC_URL}/img/signup/enterprise.svg`,
    redirect: `signup/grafana-enterprise/trial/select-org`,
    loginRedirect: `signup/grafana-enterprise/trial/select-org`,
    isPortalRedirect: true,
    signupForm: 'enterprise_trial',
    skipOrgCreation: true,
  },
  {
    key: 'frontend-observability',
    title: "See your app through your users' eyes — free forever.",
    bullets: [
      'Track Core Web Vitals, page load times, and user interactions in real time',
      'Group errors automatically and trace them to the exact line of code',
      'Correlate frontend sessions with backend traces for full-stack context',
      'Free forever access to Frontend Observability — up to 50k sessions/month',
    ],
    imgAlt: 'Frontend Observability',
    imgSrc: `${PUBLIC_URL}/img/signup/frontend-observability.svg`,
    redirect: `a/grafana-pathfinder-app?pathfinderKiosk=1&kioskRulesUrl=https://interactive-learning.grafana.net/guides/kiosk/dem/rules.json`,
  },
  {
    key: 'git-sync',
    title: 'Manage your dashboards as code — free forever.',
    bullets: [
      'Git-backed provisioning — push changes, they deploy automatically',
      'Full version history, branching, and rollback for every dashboard',
      'Grafana Assistant — AI that helps you build and debug queries faster',
      'Free forever — 10k Prometheus metrics, 50 GB logs, traces, and profiles',
    ],
    imgAlt: 'Git Sync',
    imgSrc: `${PUBLIC_URL}/img/signup/provisioning.svg`,
    isHeroImg: true,
    redirect: `connections/auth`,
    loginRedirect: `connections/provisioning`,
    isPortalRedirect: true,
  },
  {
    key: 'incident',
    title: 'Resolve incidents faster with automated workflows — free forever.',
    bullets: [
      'Automate declaration, task assignment, and comms for any severity',
      'Keep teams aligned with shared timelines and runbooks',
      'Free forever — up to 3 active IRM users/month',
    ],
    imgAlt: 'Incident',
    imgSrc: `${PUBLIC_URL}/img/signup/incident.svg`,
    redirect: `a/grafana-irm-app`,
  },
  {
    key: 'investigations',
    title: 'Accelerate incident response with AI Investigations',
    bullets: [
      'Automatically investigate metrics, logs, traces, deployments, and alerts to identify likely causes',
      'Generate and test hypotheses automatically using production telemetry',
      'Guide investigations yourself or let Grafana Assistant investigate independently and review the findings',
      'Free forever — 25M tokens/month for automated investigations, 40M tokens/user/month for ones you run yourself',
    ],
    imgAlt: 'AI Investigations',
    imgSrc: `${PUBLIC_URL}/img/signup/assistant.svg`,
    redirect: `a/grafana-assistant-app/investigations`,
  },
  {
    key: 'irm',
    title: 'Respond to incidents faster. Reduce downtime — free forever.',
    bullets: [
      'Detect and escalate issues to the right team automatically',
      'Automate incident declaration, task assignment, and stakeholder comms',
      'AI-powered insights to spot patterns across services, teams, and regions',
      'Free forever — up to 3 active IRM users/month',
    ],
    imgAlt: 'IRM',
    imgSrc: `${PUBLIC_URL}/img/signup/irm.svg`,
    redirect: `a/grafana-irm-app`,
  },
  {
    key: 'k6',
    title: 'Find performance issues before your users do — free forever.',
    bullets: [
      'Run load tests from 30+ global locations',
      'Catch regressions before they ship — CI/CD-ready CLI',
      'Correlate test results with metrics, logs, and traces in one view',
      'Free forever access to k6 Load Testing — up to 500 VUh/month',
    ],
    imgAlt: 'k6',
    imgSrc: `${PUBLIC_URL}/img/signup/k6.svg`,
    redirect: `a/k6-app/initialize`,
  },
  {
    key: 'kubernetes',
    title: 'See every pod, node, and cluster — free forever.',
    bullets: [
      'Spot problems at the highest level and drill down to the crashing pod',
      'AI-powered analysis for incidents, workload rightsizing, and cost savings',
      'Pre-built dashboards and alerts, ready in minutes',
      'Free forever access to Kubernetes Monitoring for up to 2,232 host hours & 37,944 container hours/month',
    ],
    imgAlt: 'Kubernetes',
    imgSrc: `${PUBLIC_URL}/img/signup/kubernetes.svg`,
    redirect: `a/grafana-setupguide-app/onboarding-flow/recommendations/landing/kubernetes`,
  },
  {
    key: 'oncall',
    title: 'Cut on-call toil. Sleep better — free forever.',
    bullets: [
      'On-call workflows built for engineers, not IT management tools',
      'Route alerts to the right person with smart escalation policies',
      'Free forever — up to 3 active IRM users/month',
    ],
    imgAlt: 'OnCall',
    imgSrc: `${PUBLIC_URL}/img/signup/oncall.svg`,
    redirect: `a/grafana-irm-app`,
  },
  {
    key: 'profiles',
    title: "Find the code that's burning your CPU — free forever.",
    bullets: [
      'Continuous profiling — always-on, minimal overhead',
      'Flame graphs that show exactly where time and resources go',
      'Correlate profiles with metrics, logs, and traces for faster RCA',
      'Free forever access to Grafana Cloud Profiles — up to 50 GB/month',
    ],
    imgAlt: 'Pyroscope',
    imgSrc: `${PUBLIC_URL}/img/signup/pyroscope.svg`,
    redirect: `connections/datasources/grafana-pyroscope-datasource`,
  },
  {
    key: 'grafana-pyroscope-datasource-settings',
    title: "Find the code that's burning your CPU — free forever.",
    bullets: [
      'Continuous profiling — always-on, minimal overhead',
      'Flame graphs that show exactly where time and resources go',
      'Correlate profiles with metrics, logs, and traces for faster RCA',
      'Free forever access to Grafana Cloud Profiles — up to 50 GB/month',
    ],
    imgAlt: 'Pyroscope',
    imgSrc: `${PUBLIC_URL}/img/signup/pyroscope.svg`,
    redirect: `connections/datasources/grafana-pyroscope-datasource`,
  },
  {
    key: 'slo',
    title: 'Stop firefighting. Monitor what actually impacts users — free forever.',
    bullets: [
      'Wizard-guided SLO setup — dashboards and alerts included automatically',
      'Cut alert noise by monitoring outcomes users care about, not raw metrics',
      'Grafana Assistant — AI that correlates SLO trends with metrics, logs, and traces',
      'Free forever — up to 3 active IRM users/month',
    ],
    imgAlt: 'SLO',
    imgSrc: `${PUBLIC_URL}/img/signup/slo.svg`,
    redirect: `a/grafana-slo-app/home/insights`,
  },
  {
    key: 'synthetic-monitoring',
    title: 'Know when your services are down before your users do — free forever.',
    bullets: [
      'Simulate critical user journeys from 20+ global locations',
      'Manage checks as code — version-controlled, CI/CD-friendly',
      'Correlate synthetic results with real metrics, logs, and traces',
      'Free forever access to Synthetic Monitoring — up to 100k executions/month',
    ],
    imgAlt: 'Synthetic Monitoring',
    imgSrc: `${PUBLIC_URL}/img/signup/synthetic-monitoring.svg`,
    redirect: `a/grafana-pathfinder-app?pathfinderKiosk=1&kioskRulesUrl=https://interactive-learning.grafana.net/guides/kiosk/dem/rules.json`,
  },
  // #endregion

  // #region Partner integrations (alphabetical order by key)
  {
    key: 'amg-support',
    title: 'Get Grafana Labs support for Amazon Managed Grafana',
    bullets: [
      'Direct support from the Grafana Labs team for your Amazon Managed Grafana workspace',
      'A Grafana token to complete the upgrade in the AWS console or CLI',
      'Open and track support tickets in the Grafana Cloud portal',
    ],
    imgAlt: 'Amazon Managed Grafana',
    imgSrc: `${PUBLIC_URL}/img/signup/marketplace-aws.svg`,
    redirect: `partners/amg/support/register`,
    loginRedirect: `partners/amg/support/register`,
    isPortalRedirect: true,
    signupForm: 'marketplace_amg',
    // The AMG register endpoint creates the org (with awsCustomerId) itself, so a trial org created
    // here would leave the user with two.
    skipOrgCreation: true,
  },
  {
    key: 'supabase',
    title: 'Monitor your Supabase projects in Grafana Cloud — free forever.',
    bullets: [
      'One-click connection — Supabase metrics in Grafana in minutes',
      'Pre-built dashboards for database performance, API usage, and query health',
      'Grafana Assistant — AI that correlates your Supabase data with logs and traces',
      'Free forever — 10k Prometheus metrics, 50 GB logs, traces, and profiles',
    ],
    imgAlt: 'Supabase',
    imgSrc: `${PUBLIC_URL}/img/signup/supabase.svg`,
    redirect: `connections/auth`,
    loginRedirect: `connections/select-org`,
    isPortalRedirect: true,
  },
  {
    key: 'vultr',
    title: 'Monitor your Vultr infrastructure in Grafana Cloud — free forever.',
    bullets: [
      'One-click connection — Vultr metrics in Grafana in minutes',
      'Pre-built dashboards for your Vultr resources',
      'Grafana Assistant — AI that correlates your Vultr data with logs and traces',
      'Free forever — 10k Prometheus metrics, 50 GB logs, traces, and profiles',
    ],
    imgAlt: 'Vultr',
    imgSrc: `${PUBLIC_URL}/img/signup/vultr.svg`,
    redirect: `connections/auth`,
    loginRedirect: `connections/select-org`,
    isPortalRedirect: true,
  },
  // #endregion

  // #region Marketplaces (alphabetical order by key)
  marketplaceSignupProduct({ key: 'marketplace-aws', provider: 'aws', name: 'AWS' }),
  marketplaceSignupProduct({ key: 'marketplace-azure', provider: 'azure', name: 'Azure' }),
  marketplaceSignupProduct({ key: 'marketplace-gcp', provider: 'gcp', name: 'Google Cloud' }),
  // #endregion

  // #region Integrations (alphabetical order by key)
  integrationSignupProduct({
    key: 'apolloserver-monitor',
    name: 'Apollo Server',
    integration: 'apollo-server',
    image: 'apollo.svg',
  }),
  integrationSignupProduct({
    key: 'asterisk-monitor',
    name: 'Asterisk',
    integration: 'asterisk',
    image: 'asterisk.png',
  }),
  integrationSignupProduct({
    key: 'ciliumenterprise-monitor',
    name: 'Cilium Enterprise',
    integration: 'cilium-enterprise',
    image: 'cilium-solo.svg',
  }),
  integrationSignupProduct({
    key: 'confluent-cloud-monitor',
    name: 'Confluent Cloud',
    integration: 'confluent-cloud',
    image: 'confluent-cloud.png',
  }),
  integrationSignupProduct({
    key: 'docker-monitor',
    name: 'Docker',
    integration: 'docker',
    image: 'docker.png',
  }),
  integrationSignupProduct({
    key: 'jenkins-monitor',
    name: 'Jenkins',
    integration: 'jenkins',
    image: 'jenkins.png',
  }),
  integrationSignupProduct({
    key: 'jira-monitor',
    name: 'Jira',
    integration: 'jira',
    image: 'jira.png',
  }),
  integrationSignupProduct({
    key: 'kafka-monitor',
    name: 'Kafka',
    integration: 'kafka',
    image: 'kafka.svg',
  }),
  integrationSignupProduct({
    key: 'linux-monitor',
    name: 'Linux',
    integration: 'linux-node',
    image: 'linux.png',
  }),
  integrationSignupProduct({
    key: 'macos-monitor',
    name: 'MacOS',
    integration: 'macos-node',
    image: 'apple.svg',
  }),
  integrationSignupProduct({
    key: 'mysql-monitor',
    name: 'MySQL',
    integration: 'mysql',
    image: 'mysql.png',
  }),
  integrationSignupProduct({
    key: 'nodejs-monitor',
    name: 'Node.js',
    integration: 'nodejs',
    image: 'nodejs.svg',
  }),
  integrationSignupProduct({
    key: 'postgresql-monitor',
    name: 'PostgreSQL',
    integration: 'postgres',
    image: 'postgres.svg',
  }),
  integrationSignupProduct({
    key: 'redis-monitor',
    name: 'Redis',
    integration: 'redis',
    image: 'redis.png',
  }),
  integrationSignupProduct({
    key: 'windows-monitor',
    name: 'Windows',
    integration: 'windows-exporter',
    image: 'windows.png',
  }),
  // #endregion

  // #region Other variants (custom order)
  defaultSignupProduct({ key: 'pg=play', redirect: `a/grafana-setupguide-app/onboarding-flow` }),
  defaultSignupProduct({ key: 'oss-migration', redirect: `admin/migrate-to-cloud` }),
  {
    key: 'cloud-auth',
    title: 'Effortless user access and team management at scale',
    description:
      'Grafana Cloud helps you stay organized and control access with powerful, built-in authentication features like:',
    bullets: [
      <>
        SAML and LDAP
        <small style={{ display: 'block' }}>
          Seamless single sign-on so your users can sign in with the tools they already use.
        </small>
        <small style={{ display: 'block' }}>
          <Link
            type="external"
            label="Learn more"
            to="https://grafana.com/docs/grafana/latest/setup-grafana/configure-security/configure-authentication/"
          />
        </small>
      </>,
      <>
        SCIM and Team Sync
        <small style={{ display: 'block' }}>
          Automatically keep teams and users in sync with your identity provider, including instant access revocation.
        </small>
        <small style={{ display: 'block' }}>
          <Link
            type="external"
            label="Learn more"
            to="https://grafana.com/docs/grafana/latest/setup-grafana/configure-security/configure-scim-provisioning/"
          />
        </small>
      </>,
      <>
        RBAC
        <small style={{ display: 'block' }}>
          Give users only the access they need with fine-grained permissions across dashboards, data, and features.
        </small>
        <small style={{ display: 'block' }}>
          <Link
            type="external"
            label="Learn more"
            to="https://grafana.com/docs/grafana/latest/administration/roles-and-permissions/access-control/"
          />
        </small>
      </>,
    ],
    imgAlt: 'Grafana Cloud',
    imgSrc: `${PUBLIC_URL}/img/signup/hero/multiverse.svg`,
    redirect: `admin/access`,
    isHeroImg: true,
  },
  {
    key: 'refCode=gr86qFVbDQQCbxR',
    title: (
      <>
        RAISE AI Hackathon 2026
        <br />
        July 4&ndash;5, Paris
      </>
    ),
    description:
      'Grafana Cloud is yours to build with at RAISE. Full-stack observability for your apps, agents, and LLMs, with Actually Useful AI™ built in.',
    bullets: [
      "Monitor your app, infra, and front-end in one place, debug fast, set alerts, and know exactly what's happening before you demo.",
      "Go further — embed dashboards, trigger actions from alerts, or run Grafana as your project's live UI.",
      'RAISE AI Hackathon offer — 30-day unlimited trial. Get full access to metrics, logs, traces, profiles, k6 load testing, synthetic monitoring, frontend observability, and all AI features.',
    ],
    footer: (
      <>
        Ready to build with Grafana at RAISE?
        <br />
        Set up in minutes. Unlimited usage for 30 days, then free forever access to 10k metrics, 50GB each for
        logs/traces/profiles, 14-day retention, and more on the free tier.
      </>
    ),
    imgAlt: 'Grafana Cloud',
    imgSrc: `${PUBLIC_URL}/img/signup/hero/multiverse.svg`,
    redirect: `a/grafana-setupguide-app/onboarding-flow`,
    isHeroImg: true,
  },
  {
    key: 'refCode=gr8yKxwrF4nQmgV',
    title: 'KubeCon Japan 2026, July 29-30',
    description:
      "Thank you for stopping by the Grafana booth! As a token of appreciation, we're offering you a complimentary 30-day Grafana Cloud Pro trial. Full-stack observability for your apps, agents, and LLMs, with Actually Useful AI™ built in.",
    bullets: [
      "Full-stack observability — Monitor your applications, infrastructure, and front-end in one place, debug fast, set alerts, and know exactly what's happening",
      'Native Kubernetes monitoring out of the box',
      'No credit card required',
    ],
    footer:
      'Set up in minutes. Unlimited usage for 30 days, then free forever access to 10k metrics, 50GB each for logs/traces/profiles, 14-day retention, and more on the free tier.',
    imgAlt: 'Sushi Grot',
    imgSrc: `${PUBLIC_URL}/img/signup/sushi-grot.svg`,
    redirect: `a/grafana-setupguide-app/onboarding-flow`,
  },
  {
    key: 'pg=solutions-digital-experience-monitoring',
    title: 'Find issues before your users do.',
    bullets: [
      'Run browser checks from 20+ global locations',
      'Analyze user actions across core user workflows',
      'See what went wrong with Session Replay',
      'Trace browser errors to backend root causes',
      'Troubleshoot faster with Grafana Assistant',
      'Free forever: 50k frontend sessions, 100k API test executions, & 10k browser test executions/month',
    ],
    imgAlt: 'Synthetic Monitoring and Frontend Observability',
    imgSrc: `${PUBLIC_URL}/img/signup/digital-experience-monitoring.svg`,
    redirect: `a/grafana-pathfinder-app?pathfinderKiosk=1&kioskRulesUrl=https://interactive-learning.grafana.net/guides/kiosk/dem/rules.json`,
  },
  // #endregion
];
